import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldAlert, Wallet } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import { Badge, Button, Field, MoneyInput, Spinner, Textarea } from '@aeris/ui'

import { ApiError, fetchCredit, setCredit } from '../lib/api.ts'
import { parseTypedMoney, toWireAmount } from '../lib/price-format.ts'

/**
 * The ceiling on fiado, on the customer's own record.
 *
 * Here rather than on a Crédito screen of its own, because it is a fact about
 * this customer and the person deciding it is already looking at them.
 *
 * The one thing this panel has to make unmissable: **an empty limit is no
 * credit, not unlimited.** Credit is granted, never presumed — so the empty
 * state says so in words rather than showing a blank field somebody reads as
 * "sem restrição".
 */

export type PartyCreditPanelProps = {
  partyId: string
  /** `finance.credit.manage`. Reading is enough to see it. */
  canManage: boolean
}

export const PartyCreditPanel = ({
  partyId,
  canManage,
}: PartyCreditPanelProps): ReactNode => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [limit, setLimit] = useState('')
  const [blockReason, setBlockReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['credit', partyId],
    queryFn: ({ signal }) => fetchCredit(partyId, signal),
  })

  const standing = query.data

  const save = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (standing === undefined) throw new Error('sem dados')
      return await setCredit(partyId, { version: standing.version, ...payload })
    },
    onSuccess: async () => {
      setError(null)
      setEditing(false)
      await queryClient.invalidateQueries({ queryKey: ['credit', partyId] })
      // The party's version moved with it, and the surrounding form holds one.
      await queryClient.invalidateQueries({ queryKey: ['party', partyId] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o crédito.'
      )
    },
  })

  if (query.isPending) {
    return (
      <div className="flex justify-center py-3">
        <Spinner />
      </div>
    )
  }

  if (standing === undefined) return null

  const hasLimit = standing.limit !== null

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
          <Wallet aria-hidden="true" className="h-3.5 w-3.5" />
          Limite de crédito (fiado) — o que o cliente pode ficar devendo
        </h3>
        {canManage && !editing ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setLimit(
                standing.limit === null
                  ? ''
                  : formatMoney(parseAmount(standing.limit)).replace('R$ ', '')
              )
              setBlockReason(standing.blockReason ?? '')
              setEditing(true)
            }}
          >
            {hasLimit ? 'Alterar' : 'Conceder crédito'}
          </Button>
        ) : null}
      </div>

      {standing.blocked ? (
        <p className="flex items-start gap-2 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)">
          <ShieldAlert
            aria-hidden="true"
            className="mt-0.5 h-3.5 w-3.5 shrink-0"
          />
          <span>
            Crédito bloqueado
            {standing.blockReason === null ? '.' : `: ${standing.blockReason}`}
          </span>
        </p>
      ) : null}

      {!hasLimit ? (
        // In words, because a blank number is read as "sem restrição" and it is
        // the opposite. This is the sentence the whole feature turns on.
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          Sem crédito concedido. Uma venda a prazo para este cliente será
          recusada até que alguém defina um limite — crédito é concedido, não
          presumido.
        </p>
      ) : (
        <div className="flex flex-wrap gap-4 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-sm)">
          <span className="text-(--text-secondary)">
            Limite{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {formatMoney(parseAmount(standing.limit ?? '0.00'))}
            </strong>
          </span>
          <span className="text-(--text-secondary)">
            Em aberto{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {formatMoney(parseAmount(standing.outstanding))}
            </strong>
          </span>
          <span className="text-(--text-secondary)">
            Disponível{' '}
            <strong className="font-(--font-mono) text-(--text-primary)">
              {formatMoney(parseAmount(standing.available))}
            </strong>
          </span>
          {standing.overdueCount > 0 ? (
            // Shown beside the limit because it overrides it: a customer inside
            // their ceiling with something late has no credit available.
            <Badge tone="danger">
              {standing.overdueCount === 1
                ? `1 título vencido há ${String(standing.oldestOverdueDays)} dias`
                : `${String(standing.overdueCount)} vencidos, o mais antigo há ${String(standing.oldestOverdueDays)} dias`}
            </Badge>
          ) : null}
        </div>
      )}

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-xs) text-(--danger)">
          {error}
        </p>
      )}

      {editing ? (
        <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2">
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label="Limite"
              hint="Vazio revoga o crédito deste cliente."
              className="w-40"
            >
              {(field) => (
                <MoneyInput {...field} value={limit} onValueChange={setLimit} />
              )}
            </Field>

            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                const typed =
                  limit.trim().length === 0 ? null : parseTypedMoney(limit)
                if (limit.trim().length > 0 && typed === null) {
                  setError('Informe um valor válido.')
                  return
                }
                save.mutate({
                  limit: typed === null ? null : toWireAmount(typed),
                })
              }}
            >
              Salvar
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false)
                setError(null)
              }}
            >
              Cancelar
            </Button>
          </div>

          {standing.blocked ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="self-start"
              disabled={save.isPending}
              onClick={() => {
                save.mutate({ blocked: false })
              }}
            >
              Desbloquear
            </Button>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <Field
                label="Motivo do bloqueio"
                hint="Obrigatório: é a primeira pergunta do caixa."
                className="min-w-56 flex-1"
              >
                {(field) => (
                  <Textarea
                    {...field}
                    rows={2}
                    value={blockReason}
                    onChange={(event) => {
                      setBlockReason(event.target.value)
                    }}
                  />
                )}
              </Field>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={save.isPending || blockReason.trim().length === 0}
                onClick={() => {
                  save.mutate({
                    blocked: true,
                    blockReason: blockReason.trim(),
                  })
                }}
              >
                Bloquear
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </section>
  )
}
